import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API } from "../App";
import { BulkRemindPanel } from "./BulkRemindPanel";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import NotifyDocumentWhatsAppDialog from "./NotifyDocumentWhatsAppDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Plane,
  Upload,
  Trash2,
  Loader2,
  RefreshCw,
  CheckCircle2,
  Clock,
  Download,
  Calendar,
  FileText,
  AlertTriangle,
  X,
  Sparkles,
  Send,
} from "lucide-react";

const formatBRL = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return iso;
  }
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
};

// =============================================================================
// UploadDialog — admin uploads N pre-filled Recibo PDFs (1 per employee)
// =============================================================================
const UploadDialog = ({ open, onClose, onCreated }) => {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  // Progress state for the long-running async upload pipeline.
  // phase: "idle" | "uploading" | "processing"
  // uploadPct: 0-100 (browser → server)
  // processed/total: page processing progress (server-side)
  const [progress, setProgress] = useState({
    phase: "idle",
    uploadPct: 0,
    processed: 0,
    total: 0,
  });
  const pollTimerRef = useRef(null);
  // Dry-run preview state: filled after parsing, before committing.
  const [preview, setPreview] = useState(null);
  // employee_ids the admin opts OUT of in the preview screen.
  const [unchecked, setUnchecked] = useState(new Set());
  const [result, setResult] = useState(null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyRecipients, setNotifyRecipients] = useState([]);
  // PDF preview viewer state
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewPart, setPreviewPart] = useState("both"); // recibo | aviso | both
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    if (open) {
      setFiles([]);
      setResult(null);
      setPreview(null);
      setUnchecked(new Set());
      setSelectedEmployeeId(null);
      setProgress({ phase: "idle", uploadPct: 0, processed: 0, total: 0 });
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cleanup the poll timer if the component unmounts while a job is
  // still being processed.
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  // Auto-select first employee when preview arrives
  useEffect(() => {
    if (preview && (preview.matched || []).length > 0 && !selectedEmployeeId) {
      setSelectedEmployeeId(preview.matched[0].employee_id);
    }
  }, [preview, selectedEmployeeId]);

  // Load the PDF for the currently selected employee + part
  useEffect(() => {
    const stagingId = preview?.staging_id;
    if (!stagingId || !selectedEmployeeId) return;
    let cancelled = false;
    setLoadingPdf(true);
    axios
      .get(
        `${API}/admin/vacation-notices/staging/${stagingId}/pdf/${selectedEmployeeId}?part=${previewPart}`,
        { responseType: "blob", timeout: 30000 },
      )
      .then((res) => {
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        setPreviewPdfUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return url;
        });
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Erro ao carregar a pré-visualização do PDF");
      })
      .finally(() => {
        if (!cancelled) setLoadingPdf(false);
      });
    return () => {
      cancelled = true;
    };
  }, [preview?.staging_id, selectedEmployeeId, previewPart]);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"),
    );
    if (!picked.length) {
      toast.error("Selecione apenas arquivos PDF");
      return;
    }
    setFiles((prev) => {
      const seen = new Set(prev.map((p) => `${p.name}-${p.size}`));
      const merged = [...prev];
      picked.forEach((p) => {
        const key = `${p.name}-${p.size}`;
        if (!seen.has(key)) {
          merged.push(p);
          seen.add(key);
        }
      });
      return merged;
    });
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (idx) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  // Step 1: upload PDFs and poll the async processing job. Shows two
  // distinct progress phases: upload (browser → server) then processing
  // (server-side page extraction + render).
  const submit = async () => {
    if (!files.length) {
      toast.error("Adicione ao menos um PDF");
      return;
    }
    setUploading(true);
    setResult(null);
    setPreview(null);
    setUnchecked(new Set());
    setProgress({ phase: "uploading", uploadPct: 0, processed: 0, total: 0 });

    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const startRes = await axios.post(
        `${API}/admin/vacation-notices/upload-async?dry_run=true`,
        fd,
        {
          timeout: 120000,
          onUploadProgress: (ev) => {
            if (!ev.total) return;
            const pct = Math.round((ev.loaded * 100) / ev.total);
            setProgress((p) => ({ ...p, phase: "uploading", uploadPct: pct }));
          },
        },
      );
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("Resposta inválida do servidor");

      setProgress((p) => ({ ...p, phase: "processing", uploadPct: 100 }));

      // Poll job status every 800ms until done or error.
      await new Promise((resolve, reject) => {
        pollTimerRef.current = setInterval(async () => {
          try {
            const r = await axios.get(
              `${API}/admin/vacation-notices/upload-job/${jobId}`,
              { timeout: 15000 },
            );
            const job = r.data || {};
            setProgress((p) => ({
              ...p,
              phase: "processing",
              processed: job.processed || 0,
              total: job.total || 0,
            }));
            if (job.status === "done") {
              clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
              const data = job.result || {};
              setPreview(data);
              if ((data.count || 0) === 0) {
                toast.error("Nenhum funcionário identificado nos PDFs.");
              }
              resolve();
            } else if (job.status === "error") {
              clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
              reject(new Error(job.error || "Erro ao processar PDFs"));
            }
          } catch (err) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
            reject(err);
          }
        }, 800);
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Falha ao processar PDFs");
    } finally {
      setUploading(false);
      setProgress({ phase: "idle", uploadPct: 0, processed: 0, total: 0 });
    }
  };

  const toggleEmployee = (employeeId) => {
    setUnchecked((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  // Step 2: confirm subset & persist them
  const confirmStaging = async () => {
    if (!preview?.staging_id) return;
    const keepIds = (preview.matched || [])
      .map((m) => m.employee_id)
      .filter((id) => !unchecked.has(id));
    if (!keepIds.length) {
      toast.error("Marque pelo menos um funcionário para enviar.");
      return;
    }
    setCommitting(true);
    try {
      const res = await axios.post(
        `${API}/admin/vacation-notices/commit-staging/${preview.staging_id}`,
        { keep_employee_ids: keepIds },
        { timeout: 60000 },
      );
      const okN = res.data?.count || 0;
      setResult(res.data);
      if (okN > 0) {
        toast.success(
          `${okN} recibo(s) criado(s) e prontos para assinatura`,
        );
        setNotifyRecipients(res.data.matched || []);
        setNotifyOpen(true);
      }
      onCreated?.();
      setPreview(null);
      setFiles([]);
      setSelectedEmployeeId(null);
      if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao confirmar envio");
    } finally {
      setCommitting(false);
    }
  };

  const cancelPreview = async () => {
    if (preview?.staging_id) {
      try {
        await axios.delete(
          `${API}/admin/vacation-notices/commit-staging/${preview.staging_id}`,
        );
      } catch {
        /* best-effort */
      }
    }
    setPreview(null);
    setUnchecked(new Set());
    setSelectedEmployeeId(null);
    if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
    setPreviewPdfUrl(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className={
          preview
            ? "max-w-6xl w-[95vw] h-[92vh] flex flex-col overflow-hidden"
            : "max-w-2xl max-h-[90vh] overflow-y-auto"
        }
        data-testid="vac-upload-dialog"
      >
        <DialogHeader className={preview ? "flex-shrink-0" : ""}>
          <DialogTitle className="flex items-center gap-2">
            <Plane className="w-4 h-4 text-sky-600" />
            {preview ? "Pré-visualização dos Recibos" : "Upload de Recibos de Férias"}
          </DialogTitle>
          {!preview && (
            <DialogDescription>
              Envie um PDF do <strong>Recibo de Férias</strong> por funcionário
              (modelo AVI Serviços). O sistema lê o CPF do documento e mostra
              uma <strong>pré-visualização</strong> antes de criar as
              solicitações de assinatura.
            </DialogDescription>
          )}
          {preview && (
            <DialogDescription>
              Confira como ficou o documento <strong>regenerado no modelo moderno</strong>.
              Clique em um funcionário à esquerda para visualizar o PDF.
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Drop zone — hidden while previewing */}
        {!preview && (
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              const dropped = Array.from(e.dataTransfer.files || []).filter(
                (f) =>
                  f.type === "application/pdf" ||
                  f.name.toLowerCase().endsWith(".pdf"),
              );
              if (dropped.length) {
                setFiles((prev) => [...prev, ...dropped]);
              }
            }}
            className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-sky-400 hover:bg-sky-50/40 transition-colors"
            data-testid="vac-upload-dropzone"
          >
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="font-medium text-slate-700">
              Arraste PDFs aqui ou clique para selecionar
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Aceita múltiplos arquivos. 1 PDF = 1 funcionário.
            </p>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="application/pdf,.pdf"
              onChange={onPick}
              className="hidden"
              data-testid="vac-upload-input"
            />
          </div>
        )}

        {/* Selected files (only before preview) */}
        {!preview && files.length > 0 && (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            <p className="text-sm font-medium">
              {files.length} arquivo(s) selecionado(s):
            </p>
            {files.map((f, i) => (
              <div
                key={`${f.name}-${i}`}
                className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm"
              >
                <FileText className="w-4 h-4 text-sky-600 flex-shrink-0" />
                <span className="flex-1 truncate">{f.name}</span>
                <span className="text-xs text-slate-500">
                  {(f.size / 1024).toFixed(1)} KB
                </span>
                <button
                  onClick={() => remove(i)}
                  className="p-1 hover:bg-slate-200 rounded"
                  aria-label="Remover"
                >
                  <X className="w-3.5 h-3.5 text-slate-500" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Progress indicator (shown while uploading or processing) */}
        {!preview && uploading && progress.phase !== "idle" && (
          <div
            className="rounded-lg border border-sky-200 bg-sky-50/60 p-4 space-y-3"
            data-testid="vac-upload-progress"
          >
            {/* Phase 1: upload bar */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="font-medium text-sky-900 flex items-center gap-1.5">
                  <Upload className="w-3.5 h-3.5" />
                  {progress.phase === "uploading"
                    ? "Enviando arquivos…"
                    : "Arquivos enviados"}
                </span>
                <span
                  className="font-mono text-sky-800"
                  data-testid="vac-upload-pct"
                >
                  {progress.uploadPct}%
                </span>
              </div>
              <div className="h-2 w-full bg-sky-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-sky-500 transition-all duration-200"
                  style={{ width: `${progress.uploadPct}%` }}
                />
              </div>
            </div>

            {/* Phase 2: server-side processing bar (only shown when known) */}
            {progress.phase === "processing" && (
              <div>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="font-medium text-emerald-900 flex items-center gap-1.5">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {progress.total > 0
                      ? `Processando recibos (${progress.processed} de ${progress.total})`
                      : "Lendo páginas do PDF…"}
                  </span>
                  <span
                    className="font-mono text-emerald-800"
                    data-testid="vac-process-pct"
                  >
                    {progress.total > 0
                      ? `${Math.round((progress.processed * 100) / progress.total)}%`
                      : ""}
                  </span>
                </div>
                <div className="h-2 w-full bg-emerald-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-emerald-500 transition-all duration-200 ${
                      progress.total === 0 ? "animate-pulse w-1/4" : ""
                    }`}
                    style={
                      progress.total > 0
                        ? {
                            width: `${Math.round(
                              (progress.processed * 100) / progress.total,
                            )}%`,
                          }
                        : undefined
                    }
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">
                  Aguarde — pode levar alguns minutos para PDFs grandes.
                </p>
              </div>
            )}
          </div>
        )}

        {/* PREVIEW state — split-pane: employee list (left) + PDF viewer (right) */}
        {preview && (
          <div className="flex-1 min-h-0 flex gap-3 mt-2" data-testid="vac-preview-block">
            {/* LEFT: employee list */}
            <div className="w-[340px] flex-shrink-0 flex flex-col gap-2 min-h-0">
              <div className="flex items-center justify-between gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-md">
                <p className="text-xs text-emerald-800 font-medium">
                  <CheckCircle2 className="inline w-3.5 h-3.5 mr-1" />
                  {(preview.matched || []).length} identificado(s)
                </p>
                <span className="text-[11px] text-emerald-700">
                  {(preview.matched || []).length - unchecked.size} marcado(s)
                </span>
              </div>

              {(preview.matched || []).length > 0 && (
                <div className="border border-slate-200 rounded-md flex-1 overflow-y-auto divide-y divide-slate-100 bg-white">
                  {preview.matched.map((m, i) => {
                    const skipped = unchecked.has(m.employee_id);
                    const isSelected = selectedEmployeeId === m.employee_id;
                    return (
                      <div
                        key={i}
                        onClick={() => setSelectedEmployeeId(m.employee_id)}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs border-l-4 transition-colors ${
                          isSelected
                            ? "bg-sky-50 border-l-sky-500"
                            : "border-l-transparent hover:bg-slate-50"
                        } ${skipped ? "opacity-50" : ""}`}
                        data-testid={`vac-preview-row-${m.matricula}`}
                      >
                        <input
                          type="checkbox"
                          checked={!skipped}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleEmployee(m.employee_id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">
                            {m.nome}
                          </p>
                          <p className="text-[10px] text-slate-500 font-mono">
                            Mat. {m.matricula}
                          </p>
                        </div>
                        {isSelected && (
                          <FileText className="w-3.5 h-3.5 text-sky-600 flex-shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {(preview.not_found || []).length > 0 && (
                <details
                  className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs"
                  data-testid="vac-preview-notfound"
                >
                  <summary className="cursor-pointer">
                    <AlertTriangle className="inline w-3.5 h-3.5 mr-1 text-amber-700" />
                    <strong>{preview.not_found.length}</strong> página(s) sem
                    funcionário
                  </summary>
                  <ul className="mt-1 space-y-0.5 ml-4 list-disc max-h-32 overflow-y-auto">
                    {preview.not_found.slice(0, 30).map((n, i) => (
                      <li key={i}>
                        <span className="font-mono">{n.filename}</span> —{" "}
                        {n.reason}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>

            {/* RIGHT: PDF viewer */}
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500">Visualizando:</span>
                <div className="inline-flex rounded-md border border-slate-200 bg-white overflow-hidden">
                  {[
                    { k: "both", label: "Completo" },
                    { k: "recibo", label: "Recibo" },
                    { k: "aviso", label: "Aviso" },
                  ].map(({ k, label }) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setPreviewPart(k)}
                      data-testid={`vac-preview-part-${k}`}
                      className={`px-2.5 py-1 text-xs transition-colors ${
                        previewPart === k
                          ? "bg-sky-600 text-white"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {loadingPdf && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                )}
                <span className="ml-auto text-[11px] text-slate-400">
                  Modelo programático moderno (regenerado)
                </span>
              </div>

              <div className="flex-1 min-h-0 border border-slate-200 rounded-md bg-slate-100 overflow-hidden">
                {previewPdfUrl ? (
                  <iframe
                    src={previewPdfUrl}
                    title="Pré-visualização do Recibo"
                    className="w-full h-full"
                    data-testid="vac-preview-pdf-iframe"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-sm text-slate-500">
                    {loadingPdf ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Carregando PDF…
                      </span>
                    ) : (
                      "Selecione um funcionário à esquerda"
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Final result — after commit */}
        {result && !preview && (
          <div className="space-y-2 border-t border-slate-200 pt-3">
            {(result.matched || []).length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-sm font-medium text-emerald-800 flex items-center gap-1.5 mb-1.5">
                  <CheckCircle2 className="w-4 h-4" />
                  {result.matched.length} recibo(s) criado(s) com sucesso
                </p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {result.matched.map((m, i) => (
                    <div key={i} className="text-xs text-emerald-700">
                      <span className="font-medium">{m.nome}</span>{" "}
                      <span className="font-mono text-emerald-600">
                        ({m.matricula})
                      </span>{" "}
                      — <span className="text-emerald-600">{m.filename}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className={preview ? "flex-shrink-0 mt-2" : ""}>
          {preview ? (
            <>
              <Button
                variant="outline"
                onClick={cancelPreview}
                disabled={committing}
                data-testid="vac-preview-cancel-btn"
              >
                <X className="w-3 h-3 mr-1" />
                Cancelar (voltar)
              </Button>
              <Button
                onClick={confirmStaging}
                disabled={
                  committing ||
                  (preview.matched || []).length === 0 ||
                  unchecked.size === (preview.matched || []).length
                }
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="vac-preview-confirm-btn"
              >
                {committing ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Enviando…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                    Confirmar envio (
                    {(preview.matched || []).length - unchecked.size})
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={uploading}
                data-testid="vac-upload-close-btn"
              >
                {result ? "Fechar" : "Cancelar"}
              </Button>
              {!result && (
                <Button
                  onClick={submit}
                  disabled={uploading || !files.length}
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid="vac-upload-submit-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Lendo PDFs…
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Pré-visualizar {files.length || ""}
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>

      <NotifyDocumentWhatsAppDialog
        open={notifyOpen}
        onOpenChange={setNotifyOpen}
        documentLabel="Recibo de Férias"
        documentType="recibo-ferias"
        recipients={notifyRecipients}
      />
    </Dialog>
  );
};

// =============================================================================
// NoticeDetailDialog — admin sees recipients status + download signed PDFs
// =============================================================================
const NoticeDetailDialog = ({ noticeId, onClose, onReload }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);

  const reload = useCallback(() => {
    if (!noticeId) return;
    setLoading(true);
    axios
      .get(`${API}/admin/vacation-notices/${noticeId}`, { timeout: 20000 })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Erro ao carregar detalhes do aviso"))
      .finally(() => setLoading(false));
  }, [noticeId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const pendingCount = (data?.recipients || []).filter(
    (r) => r.status !== "signed",
  ).length;
  const signedCount = (data?.recipients || []).filter(
    (r) => r.status === "signed",
  ).length;

  const [resending, setResending] = useState(false);
  const [confirmResend, setConfirmResend] = useState(false);

  const handleResendSigned = async () => {
    setResending(true);
    try {
      const res = await axios.post(
        `${API}/admin/vacation-notices/${noticeId}/resend-signed`,
        {},
        { timeout: 30000 },
      );
      const d = res.data || {};
      toast.success(
        d.message ||
          `${d.queued || 0} reenvio(s) iniciado(s) em background.`,
      );
      setConfirmResend(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao reenviar PDFs");
    } finally {
      setResending(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await axios.post(
        `${API}/admin/vacation-notices/${noticeId}/regenerate-pending`,
        {},
        { timeout: 180000 },
      );
      const d = res.data || {};
      const parts = [
        `${d.regenerated || 0} regenerado(s)`,
        d.skipped_signed ? `${d.skipped_signed} já assinado(s)` : null,
        d.skipped_already_modern ? `${d.skipped_already_modern} já moderno(s)` : null,
        d.errors?.length ? `${d.errors.length} erro(s)` : null,
      ].filter(Boolean);
      if ((d.regenerated || 0) > 0) {
        toast.success(parts.join(" · "));
      } else if (d.skipped_already_modern && !d.errors?.length) {
        toast.info("Todos os PDFs já estavam no modelo moderno.");
      } else {
        toast.warning(parts.join(" · ") || "Nada foi regenerado.");
      }
      setConfirmRegen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao regenerar PDFs");
    } finally {
      setRegenerating(false);
    }
  };

  const downloadPdf = async (recipientId, nome, part) => {
    try {
      const res = await axios.get(
        `${API}/admin/vacation-notices/${noticeId}/pdf/${recipientId}?part=${part}`,
        { responseType: "blob", timeout: 30000 },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${part}_ferias_${nome.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao baixar PDF");
    }
  };

  const [deletingRid, setDeletingRid] = useState(null);
  const deleteRecipient = async (r) => {
    if (!window.confirm(
      `Excluir apenas o recibo de "${r.nome}"?\n\nO restante do lote continua intacto.\nEsta ação não pode ser desfeita.`,
    )) return;
    setDeletingRid(r.recipient_id);
    try {
      await axios.delete(
        `${API}/admin/vacation-notices/${noticeId}`
        + `/recipient/${encodeURIComponent(r.recipient_id)}`,
        { timeout: 20000 },
      );
      toast.success(`Recibo de ${r.nome} excluído.`);
      // Optimistically hide the row locally so the admin sees an immediate
      // update while the outer list refetches.
      onReload?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir.");
    } finally {
      setDeletingRid(null);
    }
  };

  return (
    <Dialog open={!!noticeId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="vac-detail-dialog"
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-sky-600" />
              Detalhes do lote
            </DialogTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {data && signedCount > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmResend(true)}
                  disabled={resending}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="vac-resend-signed-btn"
                >
                  {resending ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                      Enviando…
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5 mr-1" />
                      Reenviar PDF moderno ({signedCount} assinado{signedCount === 1 ? "" : "s"})
                    </>
                  )}
                </Button>
              )}
              {data && pendingCount > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmRegen(true)}
                  disabled={regenerating}
                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                  data-testid="vac-regenerate-pending-btn"
                >
                  {regenerating ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                      Regenerando…
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3.5 h-3.5 mr-1" />
                      Regenerar PDF moderno ({pendingCount} pendente{pendingCount === 1 ? "" : "s"})
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        {loading || !data ? (
          <div className="p-8 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Carregando…
          </div>
        ) : (
          <div className="space-y-3">
            {/* Bulk cobrança WhatsApp — apenas pendentes com WhatsApp
                confirmado. Regras anti-ban ficam no backend. */}
            <BulkRemindPanel
              previewUrl={`/admin/vacation-notices/${noticeId}/bulk-remind/preview`}
              fireUrl={`/admin/vacation-notices/${noticeId}/bulk-remind`}
              hasPending={pendingCount > 0}
              docLabel="Recibo de Férias pendente"
              onDone={reload}
            />            <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 p-3 rounded-lg">
              <div>
                <p className="text-xs text-slate-500">Criado em</p>
                <p className="font-medium">{formatDateTime(data.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Funcionários</p>
                <p className="font-medium">{data.recipients?.length || 0}</p>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">Funcionários</p>
              <div className="border rounded-lg divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {(data.recipients || []).map((r) => (
                  <div
                    key={r.recipient_id || r.employee_id}
                    className="p-2.5 text-sm space-y-1.5"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{r.nome}</p>
                        <p className="text-xs text-slate-500 font-mono">
                          Matrícula {r.matricula}
                        </p>
                        {/* 2026-07-09: contact line — WhatsApp shortcut
                            when the number is confirmed, otherwise fall
                            back to the plain telefone. Kept inline so
                            the row height doesn't jump on batches. */}
                        {(() => {
                          const wa = (r.whatsapp || "").trim();
                          const tel = (r.telefone || "").trim();
                          const isWa =
                            !!wa && r.whatsapp_status === "confirmed";
                          const shown = isWa ? wa : tel;
                          if (!shown) return null;
                          if (isWa) {
                            return (
                              <a
                                href={`https://wa.me/${wa.replace(/[^0-9]/g, "")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-900 hover:underline"
                                data-testid={`vac-wa-${r.recipient_id}`}
                                title="Abrir conversa no WhatsApp"
                              >
                                <svg
                                  className="w-3 h-3 shrink-0"
                                  viewBox="0 0 24 24" fill="currentColor"
                                  aria-label="WhatsApp"
                                >
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                                  <path d="M20.52 3.449C12.831-3.984.106 1.407.101 11.893a11.9 11.9 0 0 0 1.6 5.945L0 24l6.335-1.652c9.98 5.404 22.037-1.816 22.037-10.451 0-3.176-1.237-6.165-3.485-8.416l-.043-.032zM12.05 21.785h-.005a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.981.999-3.648-.235-.374a9.86 9.86 0 0 1-1.511-5.263c.003-5.45 4.436-9.884 9.892-9.884 2.641 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.436 9.884-9.888 9.884z"/>
                                </svg>
                                <span className="font-mono">{shown}</span>
                              </a>
                            );
                          }
                          return (
                            <span
                              className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-slate-600"
                              data-testid={`vac-phone-${r.recipient_id}`}
                              title="Telefone (não confirmado como WhatsApp)"
                            >
                              <svg
                                className="w-3 h-3 shrink-0 text-slate-400"
                                fill="none" stroke="currentColor"
                                strokeWidth="2" viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round"
                                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              <span className="font-mono">{shown}</span>
                            </span>
                          );
                        })()}
                      </div>
                      {r.status === "signed" ? (
                        <>
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            {r.signed_at
                              ? formatDateTime(r.signed_at)
                              : "Assinado"}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadPdf(r.recipient_id, r.nome, "recibo")}
                            data-testid={`vac-download-recibo-${r.recipient_id}`}
                            title="Baixar Recibo"
                          >
                            <Download className="w-3.5 h-3.5 mr-1" />
                            Recibo
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadPdf(r.recipient_id, r.nome, "aviso")}
                            data-testid={`vac-download-aviso-${r.recipient_id}`}
                            title="Baixar Aviso"
                          >
                            <Download className="w-3.5 h-3.5 mr-1" />
                            Aviso
                          </Button>
                        </>
                      ) : (
                        <Badge variant="outline" className="text-amber-700">
                          <Clock className="w-3 h-3 mr-1" />
                          Pendente
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteRecipient(r)}
                        disabled={deletingRid === r.recipient_id}
                        data-testid={`vac-delete-recipient-${r.recipient_id}`}
                        title="Excluir este recibo (só do funcionário)"
                        className="h-8 border-red-200 text-red-600 hover:bg-red-50"
                      >
                        {deletingRid === r.recipient_id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                    {r.status === "signed" && (r.signature_id_recibo || r.signature_id) ? (
                      <div className="bg-slate-50 rounded p-1.5 text-xs space-y-0.5 font-mono">
                        <div>
                          <span className="text-slate-500">ID Recibo:</span>{" "}
                          <span className="font-semibold text-emerald-700">
                            {r.signature_id_recibo || r.signature_id}
                          </span>
                        </div>
                        {r.signature_id_aviso ? (
                          <div>
                            <span className="text-slate-500">ID Aviso:</span>{" "}
                            <span className="font-semibold text-sky-700">
                              {r.signature_id_aviso}
                            </span>
                          </div>
                        ) : null}
                        {r.client_ip ? (
                          <div className="text-slate-500">
                            IP {r.client_ip} •{" "}
                            {[r.client_browser, r.client_os, r.client_device]
                              .filter(Boolean)
                              .join(" • ")}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Confirm resend-signed dialog */}
      <Dialog open={confirmResend} onOpenChange={(o) => !o && !resending && setConfirmResend(false)}>
        <DialogContent data-testid="vac-resend-signed-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Send className="w-4 h-4" />
              Reenviar PDFs aos já assinados?
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <span className="block">
                Esta ação vai reenviar o PDF assinado <strong>no modelo moderno corrigido</strong>
                {" "}para os <strong>{signedCount} funcionário(s) já assinado(s)</strong> deste lote,
                tanto por <strong>WhatsApp</strong> quanto por <strong>e-mail</strong>.
              </span>
              <span className="block text-xs text-slate-600">
                ✓ Mantém o ID da assinatura, hash e data/hora originais.<br />
                ✓ O selo de auditoria (IP, dispositivo, OTP) é preservado.<br />
                ✓ Funcionários pendentes não são afetados.<br />
                ✓ Roda em background — pode demorar alguns segundos.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmResend(false)}
              disabled={resending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleResendSigned}
              disabled={resending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="vac-resend-signed-confirm-btn"
            >
              {resending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Enviando…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-1.5" />
                  Sim, reenviar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm regenerate dialog */}
      <Dialog open={confirmRegen} onOpenChange={(o) => !o && !regenerating && setConfirmRegen(false)}>
        <DialogContent data-testid="vac-regenerate-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-700">
              <Sparkles className="w-4 h-4" />
              Regenerar PDFs no modelo moderno?
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <span className="block">
                Esta ação vai reprocessar os <strong>{pendingCount} funcionário(s) pendente(s)</strong>
                {" "}deste lote: lê o PDF antigo do banco, extrai os dados (Recibo + Aviso) e
                substitui pelo modelo programático moderno.
              </span>
              <span className="block text-xs text-slate-600">
                ✓ Funcionários já assinados <strong>não são tocados</strong>.<br />
                ✓ PDFs que já estão no formato moderno são ignorados.<br />
                ✓ Nenhum WhatsApp/SMS é disparado — o novo PDF só aparece quando
                {" "}o funcionário for assinar.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmRegen(false)}
              disabled={regenerating}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRegenerate}
              disabled={regenerating}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="vac-regenerate-confirm-btn"
            >
              {regenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Processando…
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-1.5" />
                  Sim, regenerar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

// =============================================================================
// Main tab — list + actions
// =============================================================================
const AdminVacationNoticesTab = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/admin/vacation-notices`, { timeout: 20000 })
      .then((res) => setNotices(res.data || []))
      .catch(() => toast.error("Erro ao carregar avisos"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/vacation-notices/${deleteId}`, {
        timeout: 20000,
      });
      toast.success("Lote excluído");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir lote");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Plane className="w-5 h-5 text-sky-600" />
            Recibos de Férias
          </h2>
          <p className="text-sm text-slate-500">
            Faça upload dos PDFs de Recibo de Férias. Os funcionários assinam
            digitalmente via SMS.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            data-testid="vac-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Atualizar
          </Button>
          <Button
            size="sm"
            onClick={() => setUploadOpen(true)}
            data-testid="vac-upload-btn"
          >
            <Upload className="w-4 h-4 mr-1" />
            Upload de PDFs
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-sm text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
          Carregando…
        </div>
      ) : notices.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Plane className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">Nenhum lote criado</p>
            <p className="text-sm text-slate-500 mt-1">
              Clique em "Upload de PDFs" para enviar os recibos.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {notices.map((n) => {
            const pct =
              n.total > 0 ? Math.round(((n.signed || 0) / n.total) * 100) : 0;
            return (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="border-slate-200 hover:border-slate-300 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-500" />
                          {formatDateTime(n.created_at)}
                        </p>
                        {n.uploaded ? (
                          <Badge
                            variant="outline"
                            className="text-sky-700 border-sky-200 mt-1 text-xs"
                          >
                            Upload de PDFs
                          </Badge>
                        ) : null}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                        onClick={() => setDeleteId(n.id)}
                        data-testid={`vac-delete-${n.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-slate-900">
                        {Number(n.valor || 0) > 0 ? formatBRL(n.valor) : `${n.total} func.`}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {n.signed || 0}/{n.total} assinados ({pct}%)
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full mt-3"
                      onClick={() => setDetailId(n.id)}
                      data-testid={`vac-detail-${n.id}`}
                    >
                      Ver detalhes
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onCreated={load}
      />
      <NoticeDetailDialog
        noticeId={detailId}
        onClose={() => setDetailId(null)}
        onReload={load}
      />

      <Dialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Excluir lote?</DialogTitle>
            <DialogDescription>
              Esta ação remove o lote de todos os funcionários (assinados ou
              pendentes) e não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteId(null)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Excluindo…" : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVacationNoticesTab;
